import { lazy, Suspense, useMemo } from 'react';

import { BlockType, IBlockViewModel } from 'Types/Litium';

type BlockTypeUnion = `${BlockType}`;

interface BlockRendererProps {
    blocks: Array<IBlockViewModel>;
}

interface ComponentProps {
    [key: string]: unknown;
    shouldLazyLoad?: boolean;
}

const blockList: Record<BlockTypeUnion, React.FunctionComponent> = {
    Textblock: lazy(() => import(`./CustomText`)),
    Video: lazy(() => import(`./Video`)),
    BannerSliderBlock: lazy(() => import(`./BannerSlider`)),
    DynamicCreative: lazy(() => import(`./Creative`)),
    Banner: lazy(() => import(`./Slider`)),
    NewletterSignupBlock: lazy(() => import(`./NewsletterSignup`)),
    Slider: lazy(() => import(`./Slider`)),
    ProductSliderBlock: lazy(() => import(`./ProductSlider`)),
    DoubleSliderBlock: lazy(() => import(`./DoubleSlider`)),
    HorizontalParallaxBlock: lazy(() => import(`./HorizontalParallax`)),
    HtmlBlock: lazy(() => import(`./Html`)),
};

const BlockComponent = ({
    type,
    data,
    index,
}: {
    type: BlockTypeUnion;
    data: Record<string, unknown>;
    index: number;
}) => {
    const Component: React.FunctionComponent<ComponentProps> = blockList[type];
    if (!Component) {
        return;
    }

    return (
        <Suspense>
            <Component {...data} shouldLazyLoad={index !== 0} />
        </Suspense>
    );
};

const BlockRenderer = ({ blocks }: BlockRendererProps) => {
    const memoizedBlocks = useMemo(
        () =>
            blocks?.map((block, index) => (
                <div
                    className="max-w-full overflow-hidden"
                    key={index}
                    data-litium-block-id={block.litiumId}
                >
                    <BlockComponent
                        index={index}
                        type={block.blockTypeId as BlockType}
                        data={block.data as Record<string, unknown>}
                    />
                </div>
            )),
        [blocks]
    );

    return memoizedBlocks;
};

export default BlockRenderer;
